// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
/**
 * Utilities for computing bounding boxes from GeoJSON data.
 */

/**
 * @param {number} x
 * @param {number} y
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bounds
 */
function extendBounds(x, y, bounds) {
  if (x < bounds.minX) bounds.minX = x;
  if (y < bounds.minY) bounds.minY = y;
  if (x > bounds.maxX) bounds.maxX = x;
  if (y > bounds.maxY) bounds.maxY = y;
}

/**
 * @param {any} geom
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bounds
 */
function walkCoords(geom, bounds) {
  if (!geom || !geom.type) return;

  if (geom.type === "Point") {
    extendBounds(geom.coordinates[0], geom.coordinates[1], bounds);
  } else if (geom.type === "MultiPoint" || geom.type === "LineString") {
    for (const c of geom.coordinates) extendBounds(c[0], c[1], bounds);
  } else if (geom.type === "MultiLineString" || geom.type === "Polygon") {
    for (const ring of geom.coordinates) for (const c of ring) extendBounds(c[0], c[1], bounds);
  } else if (geom.type === "MultiPolygon") {
    for (const poly of geom.coordinates)
      for (const ring of poly) for (const c of ring) extendBounds(c[0], c[1], bounds);
  } else if (geom.type === "GeometryCollection") {
    for (const g of geom.geometries) walkCoords(g, bounds);
  }
}

/**
 * Calculate the bounding box of a GeoJSON FeatureCollection.
 * Returns bounds as [[minX, minY], [maxX, maxY]].
 * @param {any} geojson
 * @returns {[[number, number], [number, number]]}
 */
export function calculateBounds(geojson) {
  if (geojson.bbox) {
    return [
      [geojson.bbox[0], geojson.bbox[1]],
      [geojson.bbox[2], geojson.bbox[3]]
    ];
  }

  const b = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

  for (const feature of geojson.features) {
    if (!feature.geometry) continue;

    if (feature.bbox) {
      extendBounds(feature.bbox[0], feature.bbox[1], b);
      extendBounds(feature.bbox[2], feature.bbox[3], b);
    } else {
      walkCoords(feature.geometry, b);
    }
  }

  return [
    [b.minX, b.minY],
    [b.maxX, b.maxY]
  ];
}
